import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IModule, IRole } from '../../../models/settings.models';
import {
  AddRole,
  AddRoleError,
  AddRoleSuccess,
  DeleteRole,
  DeleteRoleSuccess,
  FetchAllModules,
  FetchAllRoles,
  SetModulesLoader,
  SetRolesLoader,
  UpdateRole,
  UpdateRoleError,
  UpdateRoleSuccess
} from '../../../store/settings.actions';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { CommonService } from 'src/app/shared/common.service';
import { AlertEnums } from 'src/app/shared/enums/shared.enums';
import { Actions, ofType } from '@ngrx/effects';
import { getUserColor, getUserInitials } from 'src/app/features/smart-inbox/utils/user.util';
import { RoleFormDialogComponent, RoleFormPayload } from './role-form-dialog/role-form-dialog.component';
import { Router } from '@angular/router';
import { countSelectedRoleModules, RolePermissionModuleView, selectRolePermissionCatalog } from '../../../store/settings-rbac.selectors';

type SortDirection = 'asc' | 'desc';
type SortField = 'name' | 'type' | 'features' | 'default_scope' | 'users';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
})
export class RolesComponent implements OnInit, OnDestroy {
  // ── Store observables ─────────────────────────────────────────────────────
  readonly roles$: Observable<IRole[]> = this.store.select(state => state.settings.allRoles);
  readonly permissionCatalog$: Observable<RolePermissionModuleView[]> = this.store.select(selectRolePermissionCatalog);

  // ── Readonly refs ─────────────────────────────────────────────────────────
  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;

  // ── Template state ────────────────────────────────────────────────────────
  sortField: SortField | null = null;
  sortMode: SortDirection = 'asc';

  // ── Private state ─────────────────────────────────────────────────────────
  private readonly sortField$ = new BehaviorSubject<SortField | null>(null);
  private readonly sortMode$ = new BehaviorSubject<SortDirection>('asc');
  private readonly destroy$ = new Subject<void>();
  private roleFormDialogRole: IRole | null = null;
  private roleFormDialogRef: MatDialogRef<RoleFormDialogComponent> | null = null;
  private permissionCatalog: RolePermissionModuleView[] = [];
  readonly sortedRoles$: Observable<IRole[]> = combineLatest([
    this.roles$,
    this.sortField$,
    this.sortMode$,
  ]).pipe(
    map(([roles, sortField, sortMode]) => {
      if (!roles?.length) return [];
      if (!sortField) return [...roles];
      return this.sortRoles(roles, sortField, sortMode);
    })
  );

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private commonService: CommonService,
    private actions$: Actions,
    private router: Router,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.store.dispatch(SetRolesLoader({ isLoading: true }));
    this.store.dispatch(FetchAllRoles());
    this.store.dispatch(SetModulesLoader({ isLoading: true }));
    this.store.dispatch(FetchAllModules());

    this.permissionCatalog$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissionCatalog) => {
        this.permissionCatalog = permissionCatalog ?? [];
      });

    this.actions$
      .pipe(ofType(DeleteRoleSuccess), takeUntil(this.destroy$))
      .subscribe(() => this.store.dispatch(FetchAllRoles()));

    this.actions$
      .pipe(ofType(AddRoleSuccess), takeUntil(this.destroy$))
      .subscribe((action) => {
        this.closeRoleFormDialog();
        if (!action.role?.id) return;
        this.router.navigate(['/settings/roles', action.role.id]);
      });

    this.actions$
      .pipe(ofType(UpdateRoleSuccess), takeUntil(this.destroy$))
      .subscribe(() => this.closeRoleFormDialog());

    this.actions$
      .pipe(ofType(AddRoleError, UpdateRoleError), takeUntil(this.destroy$))
      .subscribe(() => this.roleFormDialogRef?.componentInstance.setSubmitting(false));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Row actions ───────────────────────────────────────────────────────────
  editRole(role?: IRole): void {
    this.roleFormDialogRole = role ?? null;
    this.openRoleFormDialog();
  }

  openRoleDetails(role: IRole): void {
    this.router.navigate(['/settings/roles', role.id]);
  }

  deleteRole(role: IRole): void {
    if (role.is_protected) return;

    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (!result) return;

        if (this.getUsersCount(role) === 0) {
          this.store.dispatch(SetRolesLoader({ isLoading: true }));
          this.store.dispatch(DeleteRole({ id: role.id }));
          return;
        }

        this.store.dispatch(SetRolesLoader({ isLoading: true }));
        this.commonService.showToast(
          this.translateService.instant(`alerts.${AlertEnums.deleteEditError}`),
          'ERROR'
        );
      });
  }

  // ── Sorting ───────────────────────────────────────────────────────────────
  onSort(field: SortField): void {
    const currentSortField = this.sortField$.getValue();
    const currentSortMode = this.sortMode$.getValue();
    const nextSortField = field;
    const nextSortMode: SortDirection = currentSortField === field
      ? (currentSortMode === 'asc' ? 'desc' : 'asc')
      : 'asc';

    this.sortField = nextSortField;
    this.sortMode = nextSortMode;
    this.sortField$.next(nextSortField);
    this.sortMode$.next(nextSortMode);
  }

  sortRoles(roles: IRole[], sortField: SortField, sortDirection: SortDirection): IRole[] {
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

    return [...roles].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'type':
          aValue = a.is_protected ? 0 : 1;
          bValue = b.is_protected ? 0 : 1;
          break;
        case 'features':
          aValue = this.getRoleModulesActualCount(a);
          bValue = this.getRoleModulesActualCount(b);
          break;
        case 'default_scope':
          aValue = 'all';
          bValue = 'all';
          break;
        case 'users':
          aValue = this.getUsersCount(a);
          bValue = this.getUsersCount(b);
          break;
        default:
          return 0;
      }

      const mainComparison = this.compareSortValues(aValue, bValue);
      if (mainComparison !== 0) return mainComparison * directionMultiplier;

      const roleNameComparison = this.compareSortValues(
        a.name?.toLowerCase() || '',
        b.name?.toLowerCase() || ''
      );
      return roleNameComparison * directionMultiplier;
    });
  }

  private compareSortValues(aValue: string | number, bValue: string | number): number {
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  }

  // ── Template helpers ──────────────────────────────────────────────────────
  trackByRoleId(_: number, role: IRole): number {
    return role.id;
  }

  getUsersCount(role: IRole): number {
    if (!Array.isArray(role.users)) return 0;
    return role.users.length;
  }

  getRoleModulesActualCount(role: IRole): number {
    return countSelectedRoleModules(this.permissionCatalog, role);
  }

  getTotalModulesCount(): number {
    return this.permissionCatalog.length;
  }

  getDataScope(role: IRole): string {
    return role.permissions.find(permission => permission.name.includes('own_data')) ? 'own' : 'all';
  }

  getAssignedUserAvatarModel(name: string | undefined): { name: string } {
    return { name: name || 'User' };
  }

  private onRoleFormDialogSave(payload: RoleFormPayload): void {
    const selectedRole = this.roleFormDialogRole;

    if (selectedRole?.id) {
      this.store.dispatch(UpdateRole({
        id: selectedRole.id,
        role: {
          name: payload.name,
        },
      }));
      return;
    }

    this.store.dispatch(AddRole({
      role: {
        name: payload.name
      },
    }));
  }

  private openRoleFormDialog(): void {
    const pendingRole = this.roleFormDialogRole;
    this.closeRoleFormDialog();
    this.roleFormDialogRole = pendingRole;

    const ref = this.dialog.open(RoleFormDialogComponent, {
      data: {
        role: pendingRole,
      },
      panelClass: 'settings-user-form-dialog',
      disableClose: true,
    });
    this.roleFormDialogRef = ref;

    ref.componentInstance.save
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(payload => this.onRoleFormDialogSave(payload));

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.roleFormDialogRef === ref) {
          this.roleFormDialogRef = null;
          this.roleFormDialogRole = null;
        }
      });
  }

  private closeRoleFormDialog(): void {
    this.roleFormDialogRef?.close();
    this.roleFormDialogRef = null;
    this.roleFormDialogRole = null;
  }
}
